
import Hey from "@/lib/hey-utils";
import ReactGA from "react-ga4";

// Initialize with false and update when consent is given
let isConsentGiven = localStorage.getItem("isAllConsentGiven") === "Y";
let isInitialized = false;

// Initialize GA with your tracking ID
export const initializeGA = () => {
  if (!isConsentGiven) {
    console.debug("🚫 Google Analytics not initialized due to lack of consent");
    return;
  }
  
  if (isInitialized) {
    console.debug("🔍 Google Analytics already initialized");
    return;
  }
  
  if (!Hey.HeyUtils) {
    console.error("🚫 Google Analytics HeyUtils instance not found");
    return;
  }
  
  let trackingId = Hey.HeyUtils.getInstance().getConfig().GATackingID;
  if (trackingId) {
    try {
      ReactGA.initialize([
        {
          trackingId: trackingId
        }
      ])
      ReactGA.gtag('js', new Date());
      ReactGA.gtag('config', trackingId);
      ReactGA.gtag('consent', 'default', {
        'ad_storage': 'granted',
        'ad_user_data': 'granted',
        'ad_personalization': 'granted',
        'analytics_storage': 'granted'
      });
      
      console.debug("🔍 Google Analytics initialized with ID:", trackingId);
      isInitialized = true;
    } catch (error) {
      console.error("🚫 Error initializing Google Analytics:", error);
    }
  } else {
    console.error("🚫 Google Analytics tracking ID not found");
  }
};

// Get device type for analytics segmentation
export const getDeviceType = (): "mobile" | "desktop" => {
  return window.innerWidth < 768 ? "mobile" : "desktop";
};

// Page view tracking with device type
export const trackPageView = (path: string) => {
  if (!isConsentGiven) return;
  if (!isInitialized) {
    initializeGA();
  }
  
  try {
    ReactGA.set({ dimension1: getDeviceType() }); // Set custom dimension for device type
    ReactGA.send({ hitType: "pageview", page: path });
    console.debug("📊 Page view tracked:", path, `(${getDeviceType()})`);
  } catch (error) {
    console.error("🚫 Error tracking page view:", error);
  }
};

// Event tracking with device type
export const trackEvent = (
  category: string,
  action: string,
  label?: string,
  value?: number
) => {
  if (!isConsentGiven) return;
  if (!isInitialized) {
    initializeGA();
  }
  const deviceType = getDeviceType();
  ReactGA.event({
    category: `${category}_${deviceType}`,
    action,
    label,
    value: value ? value : 1,
  });
  console.debug("🎯 Event tracked:", { category, action, label, value, deviceType });
};

// User tracking
export const trackUser = (userId: string) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  ReactGA.set({ userId, dimension1: deviceType });
  console.debug("👤 User tracked:", userId, `(${deviceType})`);
};

// Trip addition result tracking
export const trackTripAdditionResult = (
  result: "success" | "profile_mismatch" | "error",
  referenceOrName: string,
  details?: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "TripAddition",
    `${result}`,
    `${referenceOrName}${details ? ` - ${details}` : ''} - ${deviceType}`,
  );
  console.debug("🧳 Trip addition result tracked:", { result, referenceOrName, details, deviceType });
};

// Trip card action tracking
export const trackTripCardAction = (
  actionType: "currency" | "weather" | "lounge" | "taxi" | "activities" | "food" | "flight_status" | "check_in" | "add_to_calendar" | "share" | "view_details" | "view_timeline" | "e_ticket" | "expand_details" | "collapse_details",
  tripId: string,
  destination: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "TripCard",
    `${actionType}_button_click`,
    `${destination} (${tripId}) - ${deviceType}`,
  );
  console.debug("🔘 Trip card action tracked:", { actionType, tripId, destination, deviceType });
};

// Timeline action tracking
export const trackTimelineAction = (
  actionType: "expand_element" | "check_flight_status" | "add_to_calendar" | "view_details" | "view_on_map" | "scroll_trips" | "filter_trips",
  elementType: "flight" | "hotel" | "car" | "activity" | "timeline" | "all",
  tripId: string,
  destination: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "Timeline",
    `${actionType}_click`,
    `${elementType} - ${destination} (${tripId}) - ${deviceType}`,
  );
  console.debug("⏱️ Timeline action tracked:", { actionType, elementType, tripId, destination, deviceType });
};

// Bottom navigation tracking
export const trackNavigation = (destination: string) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "Navigation",
    "bottom_nav_click",
    `${destination} - ${deviceType}`
  );
  console.debug("🧭 Navigation tracked:", destination, `(${deviceType})`);
};

// Header menu tracking
export const trackHeaderAction = (
  actionType: "notifications" | "sign_out" | "upcoming_flights" | "menu_toggle" | "profile" | "new_trip" | "search",
  details?: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "Header",
    `${actionType}_click`,
    `${details ? details + ' - ' : ''}${deviceType}`
  );
  console.debug("🔝 Header action tracked:", { actionType, details, deviceType });
};

// Auth action tracking
export const trackAuthAction = (
  actionType: "password_login" | "otp_request" | "otp_verify" | "register_start" | "register_complete" | "switch_method" | "forgot_password" | "social_login" | "logout" | "terms_click" | "otp_code_from_button",
  status: "success" | "failed" = "success",
  details?: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "Authentication",
    actionType,
    `${status}${details ? ': ' + details : ''} - ${deviceType}`,
  );
  console.debug("🔑 Auth action tracked:", { actionType, status, details, deviceType });
};

// Profile actions tracking
export const trackProfileAction = (
  actionType: "view_profile" | "update_profile" | "change_language" | "change_currency" | 
              "change_password" | "update_name" | "update_location" | "expand_section" | 
              "collapse_section" | "delete_account" | "consent_settings",
  status: "success" | "failed" = "success",
  details?: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "Profile",
    actionType,
    `${status}${details ? ': ' + details : ''} - ${deviceType}`,
  );
  console.debug("👤 Profile action tracked:", { actionType, status, details, deviceType });
};

// Index page specific analytics
export const trackIndexAction = (
  actionType: "view_trip_details" | "add_new_trip" | "scroll_timeline" | "filter_trips" | "welcome_interaction" | "empty_state_action",
  tripId?: string,
  details?: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "IndexPage",
    actionType,
    `${tripId ? 'Trip: ' + tripId + ' - ' : ''}${details ? details + ' - ' : ''}${deviceType}`,
  );
  console.debug("🏠 Index action tracked:", { actionType, tripId, details, deviceType });
};

// Track error events
export const trackError = (
  errorType: "api" | "auth" | "navigation" | "rendering" | "data_loading",
  errorMessage: string,
  componentName?: string
) => {
  if (!isConsentGiven) return;
  const deviceType = getDeviceType();
  trackEvent(
    "Error",
    errorType,
    `${componentName ? componentName + ': ' : ''}${errorMessage} - ${deviceType}`,
  );
  console.debug("❌ Error tracked:", { errorType, errorMessage, componentName, deviceType });
};
